import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAstro, verifyAstro, deleteAstro, fetchAstroUpdateRequest, exportAstroData } from "../../Redux/Reducers/AstroReducer";
import {
  Box, CircularProgress, Switch, IconButton, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Button, Tabs, Tab, Tooltip, TextField
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from '../../context/AuthContext';

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

function AstrologerList() {
  const { can } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const debouncedStartDate = useDebounce(startDate, 500);
  const debouncedEndDate = useDebounce(endDate, 500);

  const [astroPaginationModel, setAstroPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [editablePaginationModel, setEditablePaginationModel] = useState({ page: 0, pageSize: 10 });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterExcludeHiddenColumns: true,
    quickFilterValues: [searchValue],
  });

  const [tabValue, setTabValue] = useState('astro');
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef();
  const isInitialMount = useRef(true);

  const {
    astroData = [],
    editableAstroData = [],
    pagination,
    editablePagination,
    loading: astroLoading
  } = useSelector(state => state?.AstroReducer || {});

  useEffect(() => {
    setLoading(astroLoading);
  }, [astroLoading]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (tabValue === 'astro') {
      setAstroPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [debouncedSearchValue, tabValue]);


  useEffect(() => {
    if (tabValue === 'astro') {
      dispatch(fetchAstro({
        page: astroPaginationModel.page + 1,
        limit: astroPaginationModel.pageSize,
        search: debouncedSearchValue,
        startDate: debouncedStartDate,
        endDate: debouncedEndDate,
      }));
    } else {
      dispatch(fetchAstroUpdateRequest({
        page: editablePaginationModel.page + 1,
        limit: editablePaginationModel.pageSize
      }));
    }
  }, [
    dispatch,
    tabValue,
    astroPaginationModel,
    editablePaginationModel,
    debouncedSearchValue,
    debouncedStartDate,
    debouncedEndDate
  ]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error("No file selected");
      return;
    }
    const token = localStorage.getItem("vedhvidhi-admin-token");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/uploadAstrologers`, {
        method: "POST",
        headers: { Authorization: token },
        body: formData,
      });
      const result = await response.json();
      if (response.ok) {
        toast.success("Astrologers uploaded successfully!");
        dispatch(fetchAstro({ page: 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
      } else {
        toast.error(result.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Something went wrong while uploading");
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleExport = async () => {
    try {
      const result = await dispatch(exportAstroData({
        search: debouncedSearchValue,
        startDate: startDate,
        endDate: endDate,
      })).unwrap();

      if (result?.data && result.data.length > 0) {
        // Convert data to CSV
        const headers = ['S.No', 'Full Name', 'Email', 'Phone Number', 'City', 'State', 'Pincode', 'Creator Name','Created At'];
        const csvContent = [
          headers.join(','),
          ...result.data.map((astro, index) => [
            index + 1,
            astro.fullName,
            astro.email,
            astro.mobileNumber,
            astro.city,
            astro.state,
            astro.pincode,
            astro.creatorName,
            new Date(astro.createdAt).toLocaleString(),
          ].join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `astrologers_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Astrologers exported successfully!');
      } else {
        toast.warning('No data to export');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export astrologers');
    }
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setStartDate('');
    setEndDate('');
    setAstroPaginationModel(prev => ({ ...prev, page: 0 }));

    // immediately refetch first page without filters
    dispatch(fetchAstro({
      page: 1,
      limit: astroPaginationModel.pageSize,
      search: '',
      startDate: '',
      endDate: '',
    }));
  };

  const handleToggle = (id) => {
    dispatch(verifyAstro({ id })).then(() => {
      dispatch(fetchAstro({ page: astroPaginationModel.page + 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
    });
  };

  const handleClickOpen = (userId) => setSelectedUserId(userId) || setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDelete = () => {
    if (selectedUserId) {
      dispatch(deleteAstro(selectedUserId)).then(() => {
        dispatch(fetchAstro({ page: astroPaginationModel.page + 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
        setOpen(false);
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setSearchValue('');
    setFilterModel(prev => ({ ...prev, quickFilterValues: [''] }));
    setTabValue(newValue);
  };

  const handleFeaturedToggle = async (rowId, currentStatus) => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/markFeaturedAstro/${rowId}`,
        {
          method: "POST",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isFeatured: !currentStatus }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast.success("Featured status updated");
        dispatch(fetchAstro({ page: astroPaginationModel.page + 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
      } else {
        toast.error(result.message || "Failed to update");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      toast.error("API error on featured toggle");
    }
  };

  const handleServiceEnabledToggle = async (rowId, currentStatus) => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/toggleServicesDisabled/${rowId}`,
        {
          method: "PATCH",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isServicesDisabledByAdmin: !currentStatus }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        const newStatus = !currentStatus;
        toast.success(newStatus ? "Services disabled successfully" : "Services enabled successfully");
        dispatch(fetchAstro({ page: astroPaginationModel.page + 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
      } else {
        toast.error(result.message || "Failed to update service status");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      toast.error("Failed to update service status");
    }
  };

  const handleLiveStreamEnabledToggle = async (rowId, currentStatus) => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/manageLiveStream/${rowId}`,
        {
          method: "POST",
          headers: { Authorization: token, "Content-Type": "application/json" },
          body: JSON.stringify({ isLiveStreamEnabled: !currentStatus }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast.success("Live stream status updated");
        dispatch(fetchAstro({ page: astroPaginationModel.page + 1, limit: astroPaginationModel.pageSize, search: debouncedSearchValue }));
      } else {
        toast.error(result.message || "Failed to update live stream status");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      toast.error("API error on live stream toggle");
    }
  };


  const canEdit = can('astrologers:edit');
  const canDelete = can('astrologers:delete');
  const canApprove = can('astrologers:approve');

  const showActions = canEdit || canDelete;

  const columns = [
    { field: 'sono', headerName: 'S.No', width: 80, filterable: false, sortable: false },
    {
      field: 'fullName',
      headerName: 'Full Name',
      width: 200,
      renderCell: (params) => {
        const isBankPending = params.row?.isBankDetailGiven; // safer condition
        return (
          <Button
            onClick={() => navigate(`/booking-by-astro/${params.row.id}`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textTransform: 'none',
              mt: 1,
            }}
          >
            {isBankPending && (
              <i className="icofont-warning-alt text-danger" title="Bank details pending"></i>
            )}
            {params.value}
          </Button>
        );
      },
    },
    { field: 'profileVisitCount', headerName: 'No. Of Profile Visit', width: 150 },
    { field: 'email', headerName: 'Email', width: 250 },
    { field: 'mobileNumber', headerName: 'Phone No.', width: 250 },
    { field: 'city', headerName: 'City', width: 120 },
    { field: 'state', headerName: 'State', width: 120 },
    { field: 'pincode', headerName: 'Pincode', width: 120 },
    { field: 'aadharNo', headerName: 'Aadhar No.', width: 150 },
    { field: 'creatorName', headerName: 'Creator Name', width: 150 },
    { field: 'panCardNo', headerName: 'Pan Card No.', width: 150 },
    { field: 'address', headerName: 'Address', width: 200 },
    { field: 'createdAt', headerName: 'Created At', width: 200, type: 'dateTime' },
    { field: 'updatedAt', headerName: 'Updated At', width: 200, type: 'dateTime' },
    {
      field: 'isLiveStreamEnabled',
      headerName: 'Live Stream',
      width: 150,
      renderCell: ({ row }) => (
        <Switch
          checked={!!row.isLiveStreamEnabled}
          onChange={() => handleLiveStreamEnabledToggle(row.id, row.isLiveStreamEnabled)}
          disabled={!canApprove}
          color="primary"
        />
      ),
    },
    {
      field: 'isFeatured',
      headerName: 'Featured',
      width: 120,
      renderCell: ({ row }) => (
        <Switch
          checked={!!row.isFeatured}
          onChange={() => handleFeaturedToggle(row.id, row.isFeatured)}
          disabled={!canEdit}
          color="primary"
        />
      ),
    },
    {
      field: 'isProfileVerified',
      headerName: 'Profile Verified',
      width: 180,
      renderCell: ({ row }) => (
        <Switch
          checked={!!row.isProfileVerified}
          onChange={() => handleToggle(row.id)}
          disabled={!canApprove}
          color="primary"
        />
      ),
    },
    {
      field: 'isServicesDisabledByAdmin',
      headerName: 'Disable Service',
      width: 150,
      renderCell: ({ row }) => {
        return (
          <Switch
            checked={row.isServicesDisabledByAdmin}
            onChange={() => handleServiceEnabledToggle(row.id, row.isServicesDisabledByAdmin)}
            disabled={!canEdit}
            color="primary"
          />
        )
      },
    },
    ...(showActions
      ? [
        {
          field: 'actions',
          headerName: 'Actions',
          width: 150,
          sortable: false,
          filterable: false,
          renderCell: ({ row }) =>
            tabValue === 'editable' ? (
              canApprove && (
                <Tooltip title="View Edit Request">
                  <IconButton component={Link} to={`/astrologerreq-view/${row.id}`}>
                    <i className="icofont-eye text-danger"></i>
                  </IconButton>
                </Tooltip>
              )
            ) : (
              <>
                {canEdit && (
                  <Tooltip title="Edit Astrologer">
                    <IconButton component={Link} to={`/astrologer-edit/${row.id}`}>
                      <i className="icofont-edit text-info"></i>
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete Astrologer">
                    <IconButton onClick={() => handleClickOpen(row.id)}>
                      <i className="icofont-ui-delete text-danger"></i>
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ),
        },
      ]
      : []),
  ];

  const currentData = tabValue === 'editable' ? editableAstroData.map(item => ({ ...item.astrologerId, requestId: item._id, })) : astroData;
  const paginationModel = tabValue === 'editable' ? editablePaginationModel : astroPaginationModel;
  const setPaginationModel = tabValue === 'editable' ? setEditablePaginationModel : setAstroPaginationModel;
  const currentPagination = tabValue === 'editable' ? editablePagination : pagination;

  const rows = (currentData || [])
    .filter(astro => astro && astro._id)
    .map((astro, index) => {
      const bank = astro.bankDetails || {};
      const isBankDetailGiven = bank.verification === 'pending';

      return {
        sono: paginationModel.page * paginationModel.pageSize + index + 1,
        id: astro._id,
        fullName: astro.fullName,
        profileVisitCount: astro.profileVisitCount || 0,
        email: astro.email,
        mobileNumber: astro.mobileNumber,
        city: astro.city,
        isFeatured: astro.isFeatured || false,
        state: astro.state,
        pincode: astro.pincode,
        aadharNo: astro.aadharNo,
        creatorName: astro.creatorName,
        panCardNo: astro.panCardNo,
        address: astro.address,
        isLiveStreamEnabled: astro.isLiveStreamEnabled || false,
        isProfileVerified: astro.isProfileVerified || false,
        isServicesDisabledByAdmin: astro.isServicesDisabledByAdmin !== undefined ? astro.isServicesDisabledByAdmin : false,
        createdAt: astro.createdAt ? new Date(astro.createdAt) : null,
        updatedAt: astro.updatedAt ? new Date(astro.updatedAt) : null,
        bankDetails: bank,
        isBankDetailGiven: isBankDetailGiven,
      };
    });

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PageHeader1 righttitle="Booking" link="/booking-add" routebutton={false} pagetitle='Astrologer Management List' />
          {can('astrologers:add') && (
            <Button variant="contained" sx={{ backgroundColor: '#7258db' }} onClick={() => navigate("/astrologer-add")}>Add Astrologer</Button>
          )}
        </div>

        <div className="d-flex gap-3 align-items-end mb-3" style={{ flexWrap: 'wrap' }}>
          <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} />
          {tabValue === 'astro' && (
            <>
              <TextField
                placeholder="Search by name, email, phone..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                sx={{ width: '250px' }}
              />
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ width: '200px' }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ width: '200px' }}
              />
              <Button variant="contained" color="success" onClick={handleExport} sx={{ height: '40px' }}>
                Export
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleClearFilters} sx={{ height: '40px' }}>
                Clear
              </Button>
            </>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Astrologer" value="astro" />
            <Tab value="editable" label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Edit Request
              <Button variant="contained" size="small" sx={{ minWidth: 'auto', p: '2px 6px', fontSize: '12px', backgroundColor: '#ff5252' }}>
                ({editablePagination?.totalDocs || 0})
              </Button>
            </Box>} />
          </Tabs>
          {can('astrologers:add') && (<Button variant="contained" color="secondary" onClick={triggerFileSelect}>
            Upload CSV
          </Button>)}
        </div>
        <div className="row g-0">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body" style={{ padding: 0 }}>
                <Box sx={{ width: '100%' }}>
                  <DataGrid
                    loading={loading}
                    columns={columns}
                    rows={rows}
                    disableRowSelectionOnClick
                    filterModel={filterModel}
                    onFilterModelChange={(newModel) => {
                      setFilterModel(newModel);
                      if (tabValue === 'astro') {
                        setSearchValue(newModel.quickFilterValues?.[0] || '');
                      }
                    }}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                    paginationMode="server"
                    rowCount={currentPagination?.totalDocs || 0}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    autoHeight
                    getRowId={(row) => row.id}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Astrologer</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete this astrologer?</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AstrologerList;